import classNames from 'classnames';

import React, {
  forwardRef,
  ForwardRefExoticComponent,
  HTMLAttributes,
  ReactNode,
  RefAttributes,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Icon } from '../icon';

import styles from './cloud-box.scss';
import { useMoveWidthCalculator, useSwitching, useTouchHandlers } from './hooks';

type Props = RefAttributes<HTMLDivElement> &
  HTMLAttributes<HTMLDivElement> & {
    header?: ReactNode;
    width?: string;
    dataTestId?: string;
    controls?: boolean;
    showControlsOnHover?: boolean;
    scrollOffset?: number;
  };

const COMPONENT_ID = 'modulor-cloud-box';

export const CloudBox: ForwardRefExoticComponent<Props> = forwardRef<HTMLDivElement, Props>(
  (
    {
      dataTestId = COMPONENT_ID,
      controls = true,
      showControlsOnHover = false,
      scrollOffset = 0,
      header,
      children,
      className,
      ...rest
    },
    ref,
  ) => {
    const cloudBoxRef = useRef<HTMLDivElement>(null);
    const itemsContainerRef = useRef<HTMLDivElement>(null);
    const leftControlRef = useRef<HTMLSpanElement>(null);
    const rightControlRef = useRef<HTMLSpanElement>(null);
    const cloudBoxItems = useRef(null);

    const [position, setPosition] = useState(0);
    const [lastItemVisible, setLastItemVisible] = useState(false);
    const [firstItemVisible, setFirstItemVisible] = useState(false);
    const [lastItemObserver, setLastItemObserver] = useState(null);
    const [firstItemObserver, setFirstItemObserver] = useState(null);
    const [isSwitching, handleSwitch] = useSwitching(300);

    useEffect(() => {
      cloudBoxItems.current = itemsContainerRef.current?.querySelectorAll(
        '[data-component-name="modulor-cloud-box-item"]',
      );

      if (cloudBoxItems.current.length === 1) {
        const innerBanner = itemsContainerRef.current?.querySelector<HTMLElement>(
          '[data-component-name="modulor-cloud-box-item"] > [data-component-name="modulor-banner"]',
        );

        if (innerBanner) innerBanner.style.width = '100%';
      }
    }, []);

    const updateControls = useCallback(() => {
      leftControlRef.current.style.visibility =
        firstItemVisible || !controls ? 'hidden' : 'visible';
      rightControlRef.current.style.visibility =
        lastItemVisible || !controls ? 'hidden' : 'visible';
    }, [lastItemVisible, firstItemVisible, controls]);

    const calculateMoveWidth = useMoveWidthCalculator(cloudBoxItems, cloudBoxRef, scrollOffset);

    const onLeftClick = useCallback(() => {
      if (isSwitching) return;

      const nextMoveWidth = calculateMoveWidth('left');

      if (nextMoveWidth > 0) {
        setPosition((prevPosition) => prevPosition + nextMoveWidth);
        handleSwitch();
        updateControls();
      }
    }, [isSwitching, updateControls]);

    const onRightClick = useCallback(() => {
      if (isSwitching || lastItemVisible) return;

      const nextMoveWidth = calculateMoveWidth('right');

      if (nextMoveWidth > 0) {
        setPosition((prevPosition) => prevPosition - nextMoveWidth);
        handleSwitch();
        updateControls();
      }
    }, [isSwitching, lastItemVisible, updateControls]);

    useTouchHandlers(cloudBoxRef, { onLeftClick, onRightClick });

    useImperativeHandle(ref, () => cloudBoxRef.current);

    useEffect(() => {
      const root = cloudBoxRef.current;
      let touchStartX;

      const touchStartHandler = (event) => {
        const touchObj = event.changedTouches[0];
        touchStartX = touchObj.pageX;
      };

      const touchMoveHandler = (event) => {
        event.preventDefault();
      };

      const touchEndHandler = (event) => {
        const touchObj = event.changedTouches[0];
        if (touchStartX < touchObj.pageX) {
          onLeftClick();
        } else if (touchStartX > touchObj.pageX) {
          onRightClick();
        }
      };

      root.addEventListener('touchstart', touchStartHandler);
      root.addEventListener('touchmove', touchMoveHandler);
      root.addEventListener('touchend', touchEndHandler);

      return () => {
        root.removeEventListener('touchstart', touchStartHandler);
        root.removeEventListener('touchmove', touchMoveHandler);
        root.removeEventListener('touchend', touchEndHandler);
      };
    }, [onLeftClick, onRightClick]);

    useEffect(() => {
      updateControls();
    }, [updateControls]);

    useEffect(() => {
      if (firstItemObserver !== null) {
        firstItemObserver.disconnect();
      } else {
        setFirstItemObserver(
          new IntersectionObserver(
            ([entry]) => {
              setFirstItemVisible(entry.isIntersecting);
            },
            { threshold: [1] },
          ),
        );
      }

      const first = cloudBoxItems.current[0];

      firstItemObserver?.observe(first);
    }, [firstItemObserver]);

    useEffect(() => {
      if (lastItemObserver !== null) {
        lastItemObserver.disconnect();
      } else {
        setLastItemObserver(
          new IntersectionObserver(
            ([entry]) => {
              setLastItemVisible(entry.isIntersecting);
            },
            { threshold: [1] },
          ),
        );
      }

      const last = cloudBoxItems.current[cloudBoxItems.current.length - 1];

      lastItemObserver?.observe(last);
    }, [lastItemObserver]);

    const transform = useMemo(
      () => ({
        transform: `translateX(${position}px)`,
      }),
      [position],
    );

    return (
      <div
        ref={cloudBoxRef}
        className={classNames(
          className,
          styles['cloud-box'],
          showControlsOnHover ? styles['show-controls-on-hover'] : '',
        )}
        data-testid={dataTestId}
        data-id={dataTestId}
        data-component-name={COMPONENT_ID}
        {...rest}>
        <div data-id={`${dataTestId}-header`} data-testid={`${dataTestId}-header`}>
          {header}
        </div>
        <div
          ref={itemsContainerRef}
          className={styles.container}
          style={transform}
          data-id={`${dataTestId}-items-container`}
          data-testid={`${dataTestId}-items-container`}>
          {children}
        </div>
        <span
          ref={leftControlRef}
          className={classNames(styles.control, styles.left)}
          onClick={onLeftClick}
          data-id={`${dataTestId}-button-left`}
          data-testid={`${dataTestId}-button-left`}>
          <Icon name="chevron-left" />
        </span>
        <span
          ref={rightControlRef}
          className={classNames(styles.control, styles.right)}
          onClick={onRightClick}
          data-id={`${dataTestId}-button-right`}
          data-testid={`${dataTestId}-button-right`}>
          <Icon name="chevron-right" />
        </span>
      </div>
    );
  },
);

CloudBox.displayName = 'CloudBox';
